/**
 * The »seamless input« behavior seam (the CSS lives in {@link ./styles}).
 *
 * A seamless input is an auto-resizing multi-line `<textarea class="rlhf-seam">`
 * that resizes with its content via the modern CSS `field-sizing: content`. The
 * platform is Chrome-first, so that is the whole mechanism on the happy path —
 * this module only adds a TRIVIAL fallback for browsers without `field-sizing`:
 * it reads the textarea's OWN `scrollHeight` on input (no measurement mirror, no
 * hidden clone) and pins the height. Where `field-sizing` is supported (or where
 * we cannot feature-detect, e.g. a test DOM) it is a pure no-op.
 *
 * Exposed as a lit `ref` binding so elements just spread `${seamAutoGrow()}` onto
 * the textarea; the callback identity is stable, so re-renders never re-bind.
 */

import { ref } from "@platform/web-kit/render"

/** True when the browser resizes textareas from content in pure CSS. */
const SUPPORTS_FIELD_SIZING: boolean =
  typeof CSS !== "undefined" && typeof CSS.supports === "function"
    ? CSS.supports("field-sizing", "content")
    : true // no reliable detection (e.g. test DOM) → assume Chrome-first support, skip JS

/** Pin a textarea's height to its content using its own scrollHeight (no mirror). */
function grow(ta: HTMLTextAreaElement): void {
  ta.style.height = "auto"
  ta.style.height = `${ta.scrollHeight}px`
}

/** Stable ref callback — attaches the fallback exactly once, only where needed. */
const bind = (el?: Element): void => {
  if (!el || SUPPORTS_FIELD_SIZING) return
  const ta = el as HTMLTextAreaElement
  grow(ta)
  ta.addEventListener("input", () => grow(ta))
}

/**
 * lit `ref` binding for a `.rlhf-seam` textarea — spread onto the element:
 * `<textarea class="rlhf-seam" ${seamAutoGrow()} …>`. No-op under `field-sizing`.
 */
export function seamAutoGrow() {
  return ref(bind)
}
